import { Loading } from '@affine/component';
import { ServerConfigService } from '@affine/core/modules/cloud';
import { useActionDocsWiki, useWikiStatus } from '@affine/core/modules/wiki';
import type {
  GetWikiDocByWorkspaceResponseOutputData,
  PostDocsStatusBodyAction,
} from '@docnboard/rest';
import { useLiveData, useService, WorkspaceService } from '@toeverything/infra';
import { useMemo } from 'react';

import {
  useWikiActionsConfig,
  type WikiActionConfig,
} from './use-wiki-action-config';
import { WikiAction } from './wiki-action';

export const actionsByStatus: Record<string, PostDocsStatusBodyAction[]> = {
  Published: ['unpublish'],
  Unpublished: ['publish'],
  None: ['add'],
};

export const useWikiActions = (
  docId: string,
  wikiDoc: Partial<GetWikiDocByWorkspaceResponseOutputData> | undefined
) => {
  const _workspaceService = useService(WorkspaceService);
  const _currentWorkspace = _workspaceService.workspace;

  const workspaceId = _currentWorkspace.id;

  const { onActionDocs, isPending } = useActionDocsWiki(workspaceId);

  const wikiStatusRes = useWikiStatus({
    workspaceId,
  });
  const wikiStatus = wikiStatusRes.data?.data?.enable;

  const serverConfig = useService(ServerConfigService).serverConfig;
  const wikiEnabled = useLiveData(serverConfig.features$.map(f => f?.wiki));

  const defaultAllConfig = useWikiActionsConfig();

  // Доступные действия в зависимости от статуса документа в БЗ
  const actions = actionsByStatus[wikiDoc?.status ?? 'None'];

  // Отображаем элементы управления если:
  // Включена база знаний в настройках рабочего пространства,
  // НЕ идет синхронизация (Completed) или
  // НЕТ никакого статуса (для кнопки добавления в БЗ)
  const isWikiActionsVisible =
    (wikiDoc?.syncStatus === 'Completed' || !wikiDoc?.status) &&
    wikiStatus &&
    wikiEnabled;

  const visibleActions: PostDocsStatusBodyAction[] = useMemo(
    () => (isWikiActionsVisible ? actions : []),
    [isWikiActionsVisible, actions]
  );

  // Собираем конфиги для доступных действий
  const actionConfigs: WikiActionConfig[] = useMemo(() => {
    const result = [];
    for (const action of visibleActions) {
      const findConf = defaultAllConfig.find(
        defConf => defConf.type === action
      );
      if (findConf) result.push(findConf);
    }
    return result;
  }, [visibleActions, defaultAllConfig]);

  // Формируем готовые кнопки для меню по конфигу
  const activeWikiActions = useMemo(() => {
    return actionConfigs.map(({ menuType, icon, label, type }) => (
      <WikiAction
        key={docId}
        type={menuType ?? 'default'}
        prefixIcon={isPending ? <Loading size="100%" /> : icon}
        label={label}
        disabled={isPending}
        onSelect={() => onActionDocs(docId, type)}
      />
    ));
  }, [docId, actionConfigs, onActionDocs, isPending]);

  return { activeWikiActions, actionConfigs, wikiStatus };
};
